import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Clipboard, ClipboardCheck, Truck, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useUpdateFulfillment, useDeleteFulfillment } from '../../hooks/useFulfillments.js';
import { toast } from '../../lib/toast.js';
import type { Fulfillment } from '@mayksel/types';

interface Props {
  fulfillment: Fulfillment;
  orderId: string;
}

const STATUS_COLOR: Record<string, { color: string; background: string }> = {
  pending:          { color: 'var(--color-text-secondary)', background: 'var(--color-bg-muted)' },
  label_purchased:  { color: 'var(--color-brand)',          background: 'var(--color-brand-pale)' },
  in_transit:       { color: 'var(--color-amber)',          background: 'var(--color-amber-light, #fef9c3)' },
  delivered:        { color: 'var(--color-success)',        background: 'var(--color-success-light)' },
  failed:           { color: 'var(--color-danger)',         background: 'var(--color-danger-light)' },
};

export default function FulfillmentCard({ fulfillment, orderId }: Props) {
  const { t } = useTranslation('fulfillments');
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const updateFulfillment = useUpdateFulfillment(orderId, fulfillment.id);
  const deleteFulfillment = useDeleteFulfillment(orderId);

  const statusStyle = STATUS_COLOR[fulfillment.status] ?? STATUS_COLOR['pending']!;

  function copyTracking() {
    if (!fulfillment.trackingNumber) return;
    void navigator.clipboard.writeText(fulfillment.trackingNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function markInTransit() {
    updateFulfillment.mutate({ status: 'in_transit' }, {
      onError: () => toast.error(t('errors.update_failed')),
    });
  }

  function markDelivered() {
    updateFulfillment.mutate({ status: 'delivered', deliveredAt: new Date().toISOString() }, {
      onError: () => toast.error(t('errors.update_failed')),
    });
  }

  function handleDelete() {
    deleteFulfillment.mutate(fulfillment.id, {
      onSuccess: () => toast.success(t('card.delete')),
      onError: () => toast.error(t('errors.delete_failed')),
    });
  }

  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
      background: 'var(--color-bg)', overflow: 'hidden',
    }}>
      {/* Card header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'var(--space-3) var(--space-4)',
        borderBottom: expanded ? '1px solid var(--color-border)' : 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <Truck size={16} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
              {t(`type.${fulfillment.type}`)}
              {fulfillment.carrier && (
                <span style={{ color: 'var(--color-text-secondary)', fontWeight: 'var(--font-normal)' }}>
                  {' '}— {fulfillment.carrier}
                  {fulfillment.service && ` (${fulfillment.service})`}
                </span>
              )}
            </div>
            {fulfillment.trackingNumber && (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono, monospace)' }}>
                {fulfillment.trackingNumber}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span style={{
            fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: 'var(--radius-full)',
            color: statusStyle.color, background: statusStyle.background,
          }}>
            {t(`status.${fulfillment.status}`)}
          </span>
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: 'var(--space-1)' }}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>

          {/* Ship to */}
          {fulfillment.shipToName && (
            <Detail label={t('fields.ship_to')}>
              <span>{fulfillment.shipToName}</span>
              {fulfillment.shipToCompany && <span style={{ color: 'var(--color-text-secondary)' }}>, {fulfillment.shipToCompany}</span>}
              {fulfillment.shipToAddress && (
                <div style={{ color: 'var(--color-text-secondary)' }}>
                  {fulfillment.shipToAddress.street1}
                  {fulfillment.shipToAddress.street2 ? `, ${fulfillment.shipToAddress.street2}` : ''},{' '}
                  {fulfillment.shipToAddress.city}, {fulfillment.shipToAddress.state} {fulfillment.shipToAddress.zip}
                </div>
              )}
            </Detail>
          )}

          {/* Notes */}
          {fulfillment.notes && (
            <Detail label={t('fields.notes')}>
              <span>{fulfillment.notes}</span>
            </Detail>
          )}

          {/* Timestamps */}
          {(fulfillment.shippedAt ?? fulfillment.deliveredAt) && (
            <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
              {fulfillment.shippedAt && (
                <Detail label={t('fields.shipped_at')}>
                  <span>{new Date(fulfillment.shippedAt).toLocaleDateString()}</span>
                </Detail>
              )}
              {fulfillment.deliveredAt && (
                <Detail label={t('fields.delivered_at')}>
                  <span>{new Date(fulfillment.deliveredAt).toLocaleDateString()}</span>
                </Detail>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--color-border)' }}>
            {fulfillment.labelUrl && (
              <ActionButton as="a" href={fulfillment.labelUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={14} /> {t('card.view_label')}
              </ActionButton>
            )}
            {fulfillment.trackingNumber && (
              <ActionButton onClick={copyTracking}>
                {copied ? <ClipboardCheck size={14} /> : <Clipboard size={14} />}
                {copied ? t('card.copied') : t('card.copy_tracking')}
              </ActionButton>
            )}
            {fulfillment.status === 'label_purchased' && (
              <ActionButton onClick={markInTransit} loading={updateFulfillment.isPending}>
                {t('card.mark_in_transit')}
              </ActionButton>
            )}
            {fulfillment.status === 'in_transit' && (
              <ActionButton onClick={markDelivered} loading={updateFulfillment.isPending}>
                {t('card.mark_delivered')}
              </ActionButton>
            )}
            {(fulfillment.status === 'pending' || fulfillment.status === 'failed') && (
              confirmDelete ? (
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                    {t('card.delete_confirm')}
                  </span>
                  <ActionButton onClick={handleDelete} loading={deleteFulfillment.isPending} danger>
                    <Trash2 size={14} /> Delete
                  </ActionButton>
                  <ActionButton onClick={() => setConfirmDelete(false)}>Cancel</ActionButton>
                </div>
              ) : (
                <ActionButton onClick={() => setConfirmDelete(true)} danger>
                  <Trash2 size={14} /> {t('card.delete')}
                </ActionButton>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{children}</div>
    </div>
  );
}

function ActionButton({
  children, onClick, as: Tag = 'button', href, target, rel,
  loading = false, danger = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  as?: 'button' | 'a';
  href?: string;
  target?: string;
  rel?: string;
  loading?: boolean;
  danger?: boolean;
}) {
  const style: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)',
    padding: '3px var(--space-3)',
    background: 'none',
    color: danger ? 'var(--color-danger)' : 'var(--color-text-secondary)',
    border: `1px solid ${danger ? 'var(--color-danger-light, #fecaca)' : 'var(--color-border)'}`,
    borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)',
    cursor: loading ? 'not-allowed' : 'pointer', textDecoration: 'none',
  };

  if (Tag === 'a') {
    return <a href={href} target={target} rel={rel} style={style}>{children}</a>;
  }
  return (
    <button type="button" onClick={onClick} disabled={loading} style={style}>
      {children}
    </button>
  );
}
